import { useState, type ReactNode } from "react";
import { ActivityIndicator, Pressable, type StyleProp, type ViewStyle } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { Banner, Button, Checkbox, Input } from "@visioni/a3s-ui/components";
import {
  PATRIOT_BACKFILL_LANES,
  UNDATED_PATRIOT_BACKFILL_LANES,
  defaultTimeZone,
  useBackfillRuns,
  useControlPatriotBackfill,
  useStartPatriotBackfill,
  type PatriotBackfillControl,
  type PatriotBackfillLane,
  type PatriotBackfillRun,
  type StartPatriotBackfillRequest,
  type StartPatriotBackfillResponse,
} from "@visioni/a3s-monitoring/app";
import { Text, View } from "@src/components";
import { Icon } from "@src/components/Icon";
import { FEATURE_FLAGS } from "@src/services/feature-flags/feature-flags";
import { FeatureGate } from "@src/services/feature-flags/FeatureGate";
import { showErrorToast, showSuccessToast } from "@src/utils/toast";
import {
  PATRIOT_BACKFILL_LANE_LABELS,
  backfillRequest,
  backfillRunControls,
  backfillScanLines,
  backfillSummary,
  isUndatedOnly,
} from "../backfill-request";
import { formatInstant } from "../format";
import { CursorPagerBar } from "../components/CursorPagerBar";
import { ActivationDateRangeField, type DayRangeValues } from "../components/ActivationDateRangeField";
import { PatriotToolsPage } from "../components/PatriotToolsPage";
import { QueryNotice } from "../components/QueryNotice";
import { TimeZoneField } from "../components/TimeZoneField";

/** How long the history keeps refreshing after a start, waiting for the run's record to appear. */
const AWAIT_RECORD_MS = 2 * 60_000;

const REFRESH_ICON_SIZE = 16;
/** Reserved whether or not the spinner is showing, so the button never moves. */
const REFRESH_SPINNER_SLOT = 20;

const viewerTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const DATED_LANES = PATRIOT_BACKFILL_LANES.filter((lane) => !UNDATED_PATRIOT_BACKFILL_LANES.includes(lane));

export function BackfillScreen() {
  const [pollUntil, setPollUntil] = useState<number | undefined>(undefined);

  return (
    <PatriotToolsPage
      title="Patriot backfill"
      subtitle="Copies Patriot records into a3s again over a range of time, for the kinds of record you choose. Use it to fill a gap, or to refresh records that look out of date."
    >
      <FeatureGate
        flag={FEATURE_FLAGS.PATRIOT_TOOLS_BACKFILL}
        fallback={
          <Banner
            variant="info"
            title="Patriot backfill isn't enabled for you"
            message="Ask engineering to turn on patriot_tools_backfill if you need it."
          />
        }
      >
        <View gap="3xl">
          <View style={styles.raised}>
            <BackfillForm onStarted={() => setPollUntil(Date.now() + AWAIT_RECORD_MS)} />
          </View>
          <BackfillHistory pollUntil={pollUntil} />
        </View>
      </FeatureGate>
    </PatriotToolsPage>
  );
}

function BackfillForm({ onStarted }: { onStarted: () => void }) {
  const backfill = useStartPatriotBackfill();
  const [lanes, setLanes] = useState<readonly PatriotBackfillLane[]>([]);
  const [days, setDays] = useState<DayRangeValues>({});
  const [fromTime, setFromTime] = useState("");
  const [toTime, setToTime] = useState("");
  // A backfill has no site, so Default is always the viewer's own zone.
  const [fallback] = useState(() => defaultTimeZone({ viewerTimeZone: viewerTimeZone() }));
  const [timeZone, setTimeZone] = useState(fallback.timeZone);
  const [force, setForce] = useState(false);
  const [confirmed, setConfirmed] = useState<StartPatriotBackfillRequest | undefined>(undefined);

  const { request, errors } = backfillRequest({ lanes, days, fromTime, toTime, timeZone, force });
  // Editing the form after asking to start drops the check, so what starts is always what was shown.
  const confirming = request !== undefined && JSON.stringify(confirmed) === JSON.stringify(request);
  const pristine =
    lanes.length === 0 &&
    days.from === undefined &&
    days.to === undefined &&
    fromTime === "" &&
    toTime === "" &&
    timeZone === fallback.timeZone &&
    !force;

  const setLane = (lane: PatriotBackfillLane, checked: boolean) => {
    // Kept in the reconciler's lane order, whatever order they were set in.
    setLanes((current) => PATRIOT_BACKFILL_LANES.filter((l) => (l === lane ? checked : current.includes(l))));
  };
  const start = () => {
    if (!request) return;
    backfill.mutate(request, {
      onSuccess: (started) => {
        showSuccessToast({ message: "Backfill started", description: startedDescription(started) });
        setConfirmed(undefined);
        onStarted();
      },
      onError: (error) => showErrorToast({ error }),
    });
  };

  const reset = () => {
    setLanes([]);
    setDays({});
    setFromTime("");
    setToTime("");
    setTimeZone(fallback.timeZone);
    setForce(false);
    setConfirmed(undefined);
  };

  const laneRow = (lane: PatriotBackfillLane) => (
    <CheckRow
      key={lane}
      label={PATRIOT_BACKFILL_LANE_LABELS[lane]}
      checked={lanes.includes(lane)}
      onChange={(checked) => setLane(lane, checked)}
    />
  );

  return (
    <View gap="xl">
      <FormSection
        title="Time-based records"
        description="Patriot can filter these by date and time. It records activity around the clock, so even a single day can hold a lot of records."
        style={styles.raised}
      >
        {DATED_LANES.map(laneRow)}

        <View gap="sm" style={styles.range}>
          {/* Stacked above the date range so its list opens over the fields below. */}
          <View style={styles.zone}>
            <TimeZoneField
              hasSiteFilter={false}
              value={timeZone}
              fallback={fallback}
              at={request?.start === undefined ? new Date() : new Date(request.start)}
              onChange={setTimeZone}
            />
          </View>
          <View style={styles.raised}>
            <ActivationDateRangeField value={days} onChange={setDays} placeholder="Choose the days to copy" />
          </View>
          <View flexDirection="row" gap="md">
            <View flex={1}>
              <Input
                label="From time"
                floatLabel="always"
                placeholder="00:00"
                value={fromTime}
                onChangeText={setFromTime}
                error={errors.fromTime}
                helperText="On the first day. Blank is midnight."
                size="small"
              />
            </View>
            <View flex={1}>
              <Input
                label="To time"
                floatLabel="always"
                placeholder="End of day"
                value={toTime}
                onChangeText={setToTime}
                error={errors.toTime}
                helperText="On the last day. Blank is the end of it."
                size="small"
              />
            </View>
          </View>
          {errors.range !== undefined && (
            <Text textStyle="xs/regular" style={styles.error}>
              {errors.range}
            </Text>
          )}
          <Text textStyle="xs/regular" color="textMuted">
            Days and times are read in the timezone above, 24-hour.
          </Text>
        </View>
      </FormSection>

      <FormSection
        title="Records"
        description="Patriot has no date to filter these on, so a backfill copies all of them."
      >
        {UNDATED_PATRIOT_BACKFILL_LANES.map(laneRow)}
      </FormSection>

      <FormSection title="Options">
        <CheckRow
          label="Copy every record again"
          detail="Including records a3s already has and that haven't changed. Slower; use it when stored records look wrong."
          checked={force}
          onChange={setForce}
        />
      </FormSection>

      {confirming ?
        <BackfillConfirmation
          request={request}
          timeZone={timeZone}
          starting={backfill.isPending}
          onStart={start}
          onCancel={() => setConfirmed(undefined)}
        />
      : <View flexDirection="row" gap="md">
          <Button label="Start backfill" size="sm" onPress={() => setConfirmed(request)} disabled={!request} />
          <Button label="Reset" variant="secondary" size="sm" onPress={reset} disabled={pristine} />
        </View>
      }
    </View>
  );
}

/** What the backfill will copy, checked before it starts: a run can't be undone, only stopped. */
function BackfillConfirmation({
  request,
  timeZone,
  starting,
  onStart,
  onCancel,
}: {
  request: StartPatriotBackfillRequest;
  /** The zone the range was entered in, so it reads back as typed. */
  timeZone: string;
  starting: boolean;
  onStart: () => void;
  onCancel: () => void;
}) {
  return (
    <View gap="md" style={styles.confirmation}>
      <Text textStyle="sm/semibold" color="text">
        Start this backfill?
      </Text>
      <View gap="xs">
        {backfillSummary(request, (iso) => formatInstant(iso, timeZone)).map(({ label, value }) => (
          <View key={label} flexDirection="row" gap="md">
            <Text textStyle="sm/regular" color="textMuted" style={styles.summaryLabel}>
              {label}
            </Text>
            <Text textStyle="sm/regular" color="text" style={styles.checkText}>
              {value}
            </Text>
          </View>
        ))}
      </View>
      <View flexDirection="row" gap="md">
        <Button label="Start backfill" size="sm" onPress={onStart} loading={starting} loadingText="Starting…" />
        <Button label="Cancel" variant="secondary" size="sm" onPress={onCancel} disabled={starting} />
      </View>
    </View>
  );
}

/** What a started backfill covers, and where it will show up. */
function startedDescription({ backfill }: StartPatriotBackfillResponse): string {
  const lanes = backfill.lanes.map((lane) => PATRIOT_BACKFILL_LANE_LABELS[lane]).join(", ");
  const force = backfill.force ? ", copying every record again" : "";
  return `${lanes}, ${rangeLabel(backfill)}${force}. It appears in the list below shortly.`;
}

function BackfillHistory({ pollUntil }: { pollUntil: number | undefined }) {
  const runs = useBackfillRuns({ pollUntil });
  const { theme } = useUnistyles();

  return (
    <View gap="md">
      <View flexDirection="row" alignItems="center" justifyContent="space-between" gap="xl">
        <View gap="xs">
          <Text textStyle="sm/semibold" color="text">
            Backfills, newest first
          </Text>
          {runs.fetchedAt !== undefined && (
            <Text textStyle="xs/regular" color="textMuted">
              {`Fetched at ${formatMoment(new Date(runs.fetchedAt).toISOString())}`}
            </Text>
          )}
        </View>
        {/* The list does not poll a running backfill, so this is how its
            progress is followed. The spinner is outside the button and the
            label never changes, so the button cannot move under a pointer
            already aimed at it. */}
        <View flexDirection="row" alignItems="center" gap="md">
          <View style={{ width: REFRESH_SPINNER_SLOT }} alignItems="center">
            {runs.isFetching && <ActivityIndicator size="small" color={theme.colors.textMuted} />}
          </View>
          <Button
            label="Refresh"
            variant="secondary"
            size="sm"
            onPress={runs.refetch}
            disabled={runs.isFetching}
            iconLeft={<Icon name="refresh" size={REFRESH_ICON_SIZE} color={theme.colors.text} />}
          />
        </View>
      </View>
      {runs.items === undefined ?
        <QueryNotice
          isLoading={runs.isLoading}
          error={runs.error}
          onRetry={runs.refetch}
          loadingLabel="Loading backfills…"
          errorTitle="Could not load backfills"
        />
      : runs.items.length === 0 && !runs.pager.canGoBack ?
        <Text textStyle="sm/regular" color="textMuted">
          No backfills yet.
        </Text>
      : <View gap="md">
          <View style={styles.card}>
            {runs.items.map((run, index) => (
              <BackfillRunRow key={run.runId} run={run} first={index === 0} onControlled={runs.refetch} />
            ))}
          </View>
          <CursorPagerBar
            pager={runs.pager}
            nextCursor={runs.nextCursor}
            isFetching={runs.isFetching}
            summary={`${String(runs.items.length)} backfills`}
          />
        </View>
      }
    </View>
  );
}

function BackfillRunRow({
  run,
  first,
  onControlled,
}: {
  run: PatriotBackfillRun;
  first: boolean;
  onControlled: () => void;
}) {
  return (
    <View gap="xs" style={[styles.row, first && styles.firstRow]}>
      <Text textStyle="xs/regular" color="textMuted">
        {`${formatMoment(run.startedAt)} · ${run.requestedByEmail ?? run.requestedBy ?? "Started outside Monitoring"}`}
      </Text>
      <Text textStyle="sm/regular" color="text">
        {`${run.lanes.map((lane) => PATRIOT_BACKFILL_LANE_LABELS[lane]).join(", ")} · ${rangeLabel(run)}${run.force ? " · every record again" : ""}`}
      </Text>
      <BackfillProgressLine run={run} />
      <BackfillRunControls run={run} onControlled={onControlled} />
    </View>
  );
}

const CONTROL_LABELS: Record<PatriotBackfillControl, string> = {
  pause: "Pause",
  resume: "Resume",
  stop: "Stop",
};

const CONTROLLED_MESSAGES: Record<PatriotBackfillControl, string> = {
  pause: "Backfill paused",
  resume: "Backfill resumed",
  stop: "Backfill stopped",
};

/**
 * Pause, resume and stop for a backfill that still has passes to run. Stopping
 * can't be undone, so it asks first; pausing doesn't need to.
 */
function BackfillRunControls({ run, onControlled }: { run: PatriotBackfillRun; onControlled: () => void }) {
  const control = useControlPatriotBackfill();
  const [confirmingStop, setConfirmingStop] = useState(false);
  const controls = backfillRunControls(run);
  if (controls.length === 0) return null;

  const apply = (action: PatriotBackfillControl) => {
    control.mutate(
      { runId: run.runId, action },
      {
        onSuccess: () => {
          showSuccessToast({
            message: CONTROLLED_MESSAGES[action],
            description:
              action === "resume" ?
                "It carries on within a minute."
              : "The hour being copied now finishes first, so it can take a few minutes to settle.",
          });
          setConfirmingStop(false);
          onControlled();
        },
        onError: (error) => showErrorToast({ error }),
      },
    );
  };

  if (confirmingStop) {
    return (
      <View gap="sm" style={styles.controls}>
        <Text textStyle="xs/regular" color="text">
          {"Stop this backfill? It can't be resumed. To carry on later, start a new backfill from where it got to."}
        </Text>
        <View flexDirection="row" gap="md">
          <Button
            label="Stop backfill"
            variantColor="danger"
            size="sm"
            onPress={() => apply("stop")}
            loading={control.isPending}
            loadingText="Stopping…"
          />
          <Button
            label="Cancel"
            variant="secondary"
            size="sm"
            onPress={() => setConfirmingStop(false)}
            disabled={control.isPending}
          />
        </View>
      </View>
    );
  }

  return (
    <View flexDirection="row" gap="md" style={styles.controls}>
      {controls.map((action) => (
        <Button
          key={action}
          label={CONTROL_LABELS[action]}
          variant="secondary"
          size="sm"
          onPress={() => (action === "stop" ? setConfirmingStop(true) : apply(action))}
          disabled={control.isPending}
          loading={control.isPending && control.variables.action === action}
        />
      ))}
    </View>
  );
}

/** A line per pass: running or paused and how far, retrying, done, stopped, or failed and why. */
function BackfillProgressLine({ run }: { run: PatriotBackfillRun }) {
  const lines = backfillScanLines(run, formatMoment);
  if (lines.length === 0) return null;
  return (
    <View gap="xs">
      {lines.map((line) => (
        <Text
          key={line.scan}
          textStyle="xs/regular"
          {...(line.failed ? { style: styles.error } : { color: "textMuted" })}
        >
          {`${line.label} · ${line.text}`}
        </Text>
      ))}
    </View>
  );
}

/** The span a backfill covered, or that it took every record of undated lanes. */
function rangeLabel({ lanes, start, end }: Pick<PatriotBackfillRun, "lanes" | "start" | "end">): string {
  if (isUndatedOnly(lanes)) return "all records";
  return start !== undefined && end !== undefined ? `${formatMoment(start)} to ${formatMoment(end)}` : "no range";
}

const formatMoment = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

function FormSection({
  title,
  description,
  style,
  children,
}: {
  title: string;
  description?: string;
  style?: StyleProp<ViewStyle>;
  children: ReactNode;
}) {
  return (
    <View gap="md" style={style}>
      <View gap="xs">
        <Text textStyle="sm/semibold" color="text">
          {title}
        </Text>
        {description !== undefined && (
          <Text textStyle="xs/regular" color="textMuted">
            {description}
          </Text>
        )}
      </View>
      {children}
    </View>
  );
}

function CheckRow({
  label,
  detail,
  checked,
  onChange,
}: {
  label: string;
  detail?: string | undefined;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <Pressable onPress={() => onChange(!checked)} accessibilityRole="checkbox" accessibilityState={{ checked }}>
      <View flexDirection="row" alignItems="flex-start" gap="md">
        {/* The row is the one press target: an interactive box inside it would handle the same press twice. */}
        <View pointerEvents="none">
          <Checkbox checked={checked} />
        </View>
        <View gap="xs" style={styles.checkText}>
          <Text textStyle="sm/regular" color="text">
            {label}
          </Text>
          {detail !== undefined && (
            <Text textStyle="xs/regular" color="textMuted">
              {detail}
            </Text>
          )}
        </View>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create((theme) => ({
  range: { maxWidth: 480 },
  // A dropdown opens over the siblings after it only when its ancestors stack above them.
  raised: { zIndex: 1 },
  zone: { zIndex: 2 },
  checkText: { flex: 1 },
  error: { color: theme.colors.status.error },
  card: {
    backgroundColor: theme.colors.surfaceCard,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.xl,
    overflow: "hidden",
  },
  row: {
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.lg,
    borderTopWidth: 1,
    borderColor: theme.colors.borderCard,
  },
  firstRow: { borderTopWidth: 0 },
  controls: { marginTop: theme.spacing.sm },
  confirmation: {
    maxWidth: 480,
    padding: theme.spacing.xl,
    backgroundColor: theme.colors.surfaceCard,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.xl,
  },
  summaryLabel: { width: 180 },
}));
